import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../../shared/components/common/SEO";
import "../../shared/locales/i18n";
import { fetchPortfolioCommitRanges } from "../../shared/api/portfolioCommitsClient";

import "./styles/index.css";

import { getPortfolioOverview } from "./application/useCases/getPortfolioOverview";
import { generateDynamicBackgroundTheme } from "./application/useCases/generateDynamicBackgroundTheme";
import { staticPortfolioRepository } from "./infrastructure/repositories/staticPortfolioRepository";
import { appWindows } from "./presentation/constants/content";
import { resumeProfile } from "./presentation/constants/resumeProfile";
import { useDesktopWindows } from "./presentation/hooks/useDesktopWindows";
import { renderWindowById } from "./presentation/renderers/renderWindowById";
import DesktopIconsPanel from "./presentation/components/DesktopIconsPanel";
import DesktopTaskbar from "./presentation/components/DesktopTaskbar";

function buildBackgroundStyle(theme) {
  return {
    "--bg-theme-glow-top": theme.glowTop,
    "--bg-theme-glow-bottom": theme.glowBottom,
    "--bg-theme-glow-mid": theme.glowMid,
    "--bg-theme-glow-top-pos": theme.glowTopPos,
    "--bg-theme-glow-bottom-pos": theme.glowBottomPos,
    "--bg-theme-glow-mid-pos": theme.glowMidPos,
    "--bg-theme-glow-top-size": theme.glowTopSize,
    "--bg-theme-glow-bottom-size": theme.glowBottomSize,
    "--bg-theme-glow-mid-size": theme.glowMidSize,
    "--bg-theme-base-start": theme.baseStart,
    "--bg-theme-base-end": theme.baseEnd,
    "--bg-theme-angle": theme.angle,
  };
}

function toDateValue(ymdOrYm) {
  if (!ymdOrYm || ymdOrYm === "present") {
    return Number.MAX_SAFE_INTEGER;
  }
  const normalized = ymdOrYm.length === 7 ? `${ymdOrYm}-01` : ymdOrYm;
  return new Date(`${normalized}T00:00:00`).getTime();
}

function formatTimelineDate(value, locale, presentLabel) {
  if (!value || value === "present") {
    return presentLabel;
  }
  const normalized = value.length === 7 ? `${value}-01` : value;
  const date = new Date(`${normalized}T00:00:00`);
  const hasDay = value.length === 10;
  return new Intl.DateTimeFormat(locale, hasDay ? { day: "2-digit", month: "short", year: "numeric" } : { month: "short", year: "numeric" }).format(date);
}

function buildFallbackCommitList(project, liveCommits) {
  const fallbackList = [];

  if (project.commitEnd) {
    fallbackList.push({
      sha: liveCommits.commitEndSha || null,
      date: project.commitEnd,
      message: "",
      url: "",
      author: "",
    });
  }

  if (project.commitStart && project.commitStart !== project.commitEnd) {
    fallbackList.push({
      sha: liveCommits.commitStartSha || null,
      date: project.commitStart,
      message: "",
      url: "",
      author: "",
    });
  }

  return fallbackList.filter((commit) => commit.date && commit.date !== "present");
}

export default function AIProjectsLandingPage() {
  const location = useLocation();
  const { t } = useTranslation("aiPortfolio");
  const [liveCommitRanges, setLiveCommitRanges] = useState({});
  const headerTitle = t("header.title");
  const [headerTitleLead, ...headerTitleRest] = headerTitle.split(" - ");
  const headerTitleSuffix = headerTitleRest.join(" - ");
  const canonical = `${window.location.origin}${location.pathname}${location.search}`;
  const ogImage = `${window.location.origin}/images/adslogo-min.webp`;

  const { projects, summary } = useMemo(
    () => getPortfolioOverview(staticPortfolioRepository),
    []
  );

  useEffect(() => {
    let stillMounted = true;

    fetchPortfolioCommitRanges(projects)
      .then((rangesByProjectId) => {
        if (stillMounted) {
          setLiveCommitRanges(rangesByProjectId);
        }
      })
      .catch(() => {
        // Fallback para commits estaticos quando a API nao estiver disponivel.
      });

    return () => {
      stillMounted = false;
    };
  }, [projects]);

  const localizedProjects = useMemo(
    () =>
      projects.map((project) => {
        const liveCommits = liveCommitRanges[project.id] || {};

        return {
          ...project,
          repositoryUrl: liveCommits.repositoryUrl || project.repositoryUrl || null,
          commitStart: liveCommits.commitStart || project.commitStart,
          commitEnd: liveCommits.commitEnd || project.commitEnd,
          commitStartSha: liveCommits.commitStartSha || null,
          commitEndSha: liveCommits.commitEndSha || null,
          commits:
            Array.isArray(liveCommits.commits) && liveCommits.commits.length > 0
              ? liveCommits.commits
              : buildFallbackCommitList(project, liveCommits),
          category: t(`projects.${project.id}.category`),
          pitch: t(`projects.${project.id}.pitch`),
          pitchCompact: t(`projects.${project.id}.pitchCompact`, {
            defaultValue: t(`projects.${project.id}.pitch`),
          }),
          functionalities: t(`projects.${project.id}.functionalities`),
          functionalitiesItems: t(`projects.${project.id}.functionalitiesItems`, { returnObjects: true }),
          architectureDomains: t(`projects.${project.id}.architectureDomains`),
          roadmap: t(`projects.${project.id}.roadmap`),
          roadmapTasks: t(`projects.${project.id}.roadmapTasks`, { returnObjects: true }),
          withAiLabel: `${project.withAiHours}h`,
          withoutAiLabel: `${project.withoutAiHours}h`,
        };
      }),
    [projects, t, liveCommitRanges]
  );
  const localizedProjectsById = useMemo(
    () =>
      localizedProjects.reduce((accumulator, project) => {
        accumulator[project.id] = project;
        return accumulator;
      }, {}),
    [localizedProjects]
  );
  const localizedAppWindows = useMemo(
    () =>
      appWindows.map((item) => ({
        ...item,
        label: t(`windows.${item.id}.label`),
        subtitle: t(`windows.${item.id}.subtitle`),
      })),
    [t]
  );
  const localizedTimelineData = useMemo(
    () => {
      const locale = t("taskbar.clockLocale");
      const presentLabel = t("timeline.present");

      const careerEvents = resumeProfile.experienceTimeline.flatMap((experience) => [
        (() => {
          const translatedDetails = t(`timeline.experienceDetails.${experience.id}`, {
            returnObjects: true,
            defaultValue: experience.details || [],
          });
          const details = Array.isArray(translatedDetails) ? translatedDetails : experience.details || [];
          const role = t(`timeline.experienceRoles.${experience.id}`, {
            defaultValue: experience.role,
          });

          return {
          id: `career-${experience.company}`,
          dateValue: toDateValue(experience.start),
          phase: `${formatTimelineDate(experience.start, locale, presentLabel)} - ${formatTimelineDate(
            experience.end,
            locale,
            presentLabel
          )}`,
          title: experience.company,
          description: role,
          details,
          eventType: "career",
          };
        })(),
      ]);

      const projectEvents = localizedProjects.map((project) => ({
        id: `project-${project.id}`,
        projectId: project.id,
        dateValue: toDateValue(project.commitStart),
        phase: `${formatTimelineDate(project.commitStart, locale, presentLabel)} - ${formatTimelineDate(
          project.commitEnd,
          locale,
          presentLabel
        )}`,
        title: project.name,
        description: `${t("timeline.projectFirstCommit")} ${project.commitStart} | ${t(
          "timeline.projectCurrentCommit"
        )} ${project.commitEnd}`,
        eventType: "project",
      }));

      return [...careerEvents, ...projectEvents]
        .sort((a, b) => a.dateValue - b.dateValue)
        .map(({ dateValue, ...event }) => event);
    },
    [localizedProjects, t]
  );
  const backgroundTheme = useMemo(() => generateDynamicBackgroundTheme(), []);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    window.matchMedia("(max-width: 700px)").matches
  );
  const [mobileModalWindowId, setMobileModalWindowId] = useState("");

  const {
    openWindows,
    focusedWindow,
    maximizedWindow,
    minimizedWindows,
    startMenuOpen,
    flashWindow,
    windowsContainerRef,
    windowRefs,
    setFocusedWindow,
    setStartMenuOpen,
    focusAndRevealWindow,
    openWindow,
    closeWindow,
    toggleMinimizeWindow,
    toggleMaximizeWindow,
    setMaximizedWindow,
  } = useDesktopWindows([]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 700px)");
    const handleViewportChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleViewportChange);

    return () => {
      mediaQuery.removeEventListener("change", handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setMobileModalWindowId("");
    }
  }, [isMobileViewport]);

  const closeWindowForCurrentViewport = (windowId) => {
    closeWindow(windowId);
    if (isMobileViewport) {
      setMobileModalWindowId((current) => (current === windowId ? "" : current));
    }
  };

  const openWindowForCurrentViewport = (windowId) => {
    openWindow(windowId);

    if (isMobileViewport) {
      setMaximizedWindow(windowId);
      setMobileModalWindowId(windowId);
    }
  };

  const registerWindowRef = (id, element) => {
    windowRefs.current[id] = element;
  };
  const handleOpenProjectWindow = (projectId) => {
    openWindowForCurrentViewport(`project-${projectId}`);
  };

  const renderDesktopWindow = (windowId, options = {}) =>
    renderWindowById({
      windowId,
      mobileModal: options.mobileModal,
      focusedWindow,
      flashWindow,
      maximizedWindow,
      minimizedWindows,
      setFocusedWindow,
      closeWindowForCurrentViewport,
      toggleMinimizeWindow,
      toggleMaximizeWindow,
      registerWindowRef,
      localizedProjectsById,
      localizedTimelineData,
      localizedProjects,
      summary,
      onOpenProjectWindow: handleOpenProjectWindow,
      t,
    });

  useEffect(() => {
    const container = windowsContainerRef.current;
    if (!container) {
      return undefined;
    }

    const applyWindowSpans = () => {
      const styles = window.getComputedStyle(container);
      const baseRow = parseFloat(styles.gridAutoRows) || 10;
      const rowGap = parseFloat(styles.rowGap) || 0;
      const windows = container.querySelectorAll(".desktopWindow:not(.maximized)");

      windows.forEach((windowElement) => {
        const windowHeight = windowElement.getBoundingClientRect().height;
        const span = Math.max(1, Math.ceil((windowHeight + rowGap) / (baseRow + rowGap)));
        windowElement.style.setProperty("--window-row-span", String(span));
      });
    };

    const observer = new ResizeObserver(() => {
      applyWindowSpans();
    });

    const windows = container.querySelectorAll(".desktopWindow:not(.maximized)");
    windows.forEach((windowElement) => observer.observe(windowElement));

    const frame = window.requestAnimationFrame(applyWindowSpans);
    window.addEventListener("resize", applyWindowSpans);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", applyWindowSpans);
      observer.disconnect();
    };
  }, [openWindows, maximizedWindow, minimizedWindows, flashWindow, windowsContainerRef]);

  return (
    <>
      <SEO
        title={t("seo.title")}
        description={t("seo.description")}
        keywords={t("seo.keywords")}
        canonical={canonical}
        ogTitle={t("seo.ogTitle")}
        ogDescription={t("seo.ogDescription")}
        ogType="website"
        ogUrl={canonical}
        ogImage={ogImage}
        twitterCard="summary_large_image"
        twitterTitle={t("seo.ogTitle")}
        twitterDescription={t("seo.twitterDescription")}
        twitterImage={ogImage}
      />

      <main className="aiLandingPage" style={buildBackgroundStyle(backgroundTheme)}>
        <section className="desktopStage">
          <header className="desktopTitleBar">
            <div>
              
              <h1>
                <span className="desktopTitleAccent">{headerTitleLead}</span>
                {headerTitleSuffix ? ` ${headerTitleSuffix}` : ""}
              </h1>
            </div>
            <div className="desktopSummary">
              <p className="desktopKicker">{t("header.kicker")}</p>
              <span className="desktopSummaryLead">{t("header.summaryLead")}</span>
              <span className="desktopSummarySub">
                {t("header.summarySub")}{" "}
                <a
                  className="brandNameLink"
                  href="https://andremourapassos.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  André Moura Passos
                </a>
              </span>
            </div>
          </header>

          <div className="desktopArea">
            <DesktopIconsPanel
              projects={localizedProjects}
              onOpenProjectWindow={handleOpenProjectWindow}
            />
            {!isMobileViewport && (
              <div className="desktopWindows" ref={windowsContainerRef}>
                {maximizedWindow && (
                  <button
                    type="button"
                    className="windowBackdrop"
                    aria-label={t("taskbar.closeExpanded")}
                    onClick={() => setMaximizedWindow("")}
                  />
                )}
                {openWindows.map((windowId) => renderDesktopWindow(windowId))}
              </div>
            )}
          </div>

          {isMobileViewport && mobileModalWindowId && (
            <>
              <button
                type="button"
                className="windowBackdrop mobileWindowBackdrop"
                aria-label={t("taskbar.closeExpanded")}
                onClick={() => closeWindowForCurrentViewport(mobileModalWindowId)}
              />
              <div className="mobileWindowLayer">{renderDesktopWindow(mobileModalWindowId, { mobileModal: true })}</div>
            </>
          )}

          <DesktopTaskbar
            appWindows={localizedAppWindows}
            projects={localizedProjects}
            openWindows={openWindows}
            startMenuOpen={startMenuOpen}
            onToggleStartMenu={() => setStartMenuOpen((prev) => !prev)}
            onOpenWindow={openWindowForCurrentViewport}
            onFocusWindow={(windowId) => {
              focusAndRevealWindow(windowId);
              if (isMobileViewport) {
                setMaximizedWindow(windowId);
                setMobileModalWindowId(windowId);
              }
            }}
          />
        </section>
      </main>
    </>
  );
}


